import { arrayMoveImmutable } from 'array-move';
import cloneDeep from 'lodash.clonedeep';
import { useState } from 'react';
import SortableList, { SortableItem, SortableKnob } from 'react-easy-sort';
import { useTranslation } from 'react-i18next';
import useContainChanges from '../../../../hooks/useContainChanges';
import { urlToCfit } from '../../../../utils/cfit';
import { Button, ButtonContent } from '../../../components/Button';
import { Column, Columns } from '../../../components/Columns';
import { IconDragAndDropHandle, IconDuplicate } from '../../../components/Icon';
import Loader from '../../../components/Loader';
import MediaPreview from '../../../components/MediaPreview';
import { Handle } from '../../../components/OptionListV2/wrapper';
import Panel from '../../../components/Panel';
import { FooterAction, FooterActionItem } from '../../../components/Panel/wrapper';
import { SeparatorList, SeparatorListItem } from '../../../components/SeparatorList';
import SortActions from '../../../components/SortActions';
import { Text } from '../../../components/Text';
import InlineMediaContent from '../../InlineMediaContent';

export interface SortPanelItem {
  id: string;
  title: string;
  description?: string;
  image?: string;
  order: number;
}

interface SortPanelProps {
  items: SortPanelItem[];
  isLoading: boolean;
  updateSortOrder: (payload: { [key: string]: number | undefined }) => void;
  onClose: () => void;
}

const SortPanel = ({ items, isLoading, updateSortOrder, onClose }: SortPanelProps) => {
  const { t } = useTranslation(['common', 'glossary']);
  const [state, setState] = useState<SortPanelItem[]>(items);

  const containChanges = useContainChanges(
    state.map(({ id }) => id).join('-'),
    items.map(({ id }) => id).join('-'),
  );

  const onUpdateSortOrder = () => {
    const payload: { [key: string]: number | undefined } = {};

    for (const { id } of state) {
      payload[id] = state.findIndex(({ id: itemId }) => itemId === id);
    }

    updateSortOrder(payload);
  };

  const onSortEnd = (oldIndex: number, newIndex: number) => {
    const next = arrayMoveImmutable(cloneDeep(state), oldIndex, newIndex).map((item, i) => ({
      ...item,
      order: i,
    }));
    setState(next);
  };

  const onClickUp = (i: number) => onSortEnd(i, i - 1);
  const onClickDown = (i: number) => onSortEnd(i, i + 1);

  const disabled = isLoading;
  const size = state.length;

  return (
    <Panel
      title={t('sortOrder', { ns: 'other' })}
      onClose={onClose}
      footer={
        <FooterAction>
          <FooterActionItem>
            <Button
              onClick={() => onClose()}
              title={t('close', { ns: 'common' })}
              $fullWidth
              $tone="secondary"
            >
              {t('close', { ns: 'common' })}
            </Button>
          </FooterActionItem>
          <FooterActionItem>
            <Button
              $tone="primary"
              type="submit"
              disabled={isLoading || !containChanges}
              onClick={onUpdateSortOrder}
            >
              <ButtonContent>{t('save', { ns: 'common' })}</ButtonContent>
              {isLoading && (
                <ButtonContent>
                  <Loader inlineBlock />
                </ButtonContent>
              )}
            </Button>
          </FooterActionItem>
        </FooterAction>
      }
    >
      <SortableList
        allowDrag
        onSortEnd={onSortEnd}
        lockAxis="y"
        draggedItemClassName="sortable-dragged-in-panel"
      >
        <SeparatorList>
          {state.map(({ id, title, description, image }, index) => (
            <SortableItem key={id}>
              <SeparatorListItem>
                <Columns $spacing={2} $valign="center">
                  <Column $noShrink>
                    <SortableKnob>
                      <Handle>
                        <IconDragAndDropHandle />
                      </Handle>
                    </SortableKnob>
                  </Column>
                  <Column $noShrink>
                    <SortActions
                      sortOrder={index + 1}
                      disabledUp={disabled || index === 0}
                      disabledDown={disabled || index === size - 1}
                      onClickUp={() => onClickUp(index)}
                      onClickDown={() => onClickDown(index)}
                    />
                  </Column>
                  <Column $grow>
                    <InlineMediaContent
                      media={
                        image && (
                          <MediaPreview
                            image={
                              image
                                ? {
                                    src: urlToCfit(image, { width: 225 })!,
                                    alt: title || t('logotype', { ns: 'glossary' }),
                                    contain: true,
                                  }
                                : undefined
                            }
                            icon={!image ? <IconDuplicate /> : undefined}
                          />
                        )
                      }
                    >
                      <Text $weight="bold">{title}</Text>
                      {description && (
                        <Text $tone="alt" $size="small">
                          {description}
                        </Text>
                      )}
                    </InlineMediaContent>
                  </Column>
                </Columns>
              </SeparatorListItem>
            </SortableItem>
          ))}
        </SeparatorList>
      </SortableList>
    </Panel>
  );
};

export default SortPanel;
